import { DemoH3Datum } from 'src/common-services/types/demo-types'
import { TrafficH3Datum } from 'src/common-services/types/traffic-types'

type BaseHeatmapConfig = {
  name: string
  category: string
  field: keyof DemoH3Datum | keyof TrafficH3Datum
  isTotal?: boolean
}

export const TRAFFIC_CATEGORY = 'Car Traffic'
export const AGE_CATEGORY = 'Age'
export const HOUSEHOLD_INCOME_CATEGORY = 'Household Income'
export const CONSUMER_SPEND_CATEGORY = 'Consumer Spend'

export type HeatmapConfigOption = BaseHeatmapConfig &
  (
    | {
        type: 'simple' | 'breakdown' | 'spend'
      }
    | {
        type: 'custom'
        getValue: (datum: DemoH3Datum | TrafficH3Datum) => number
      }
  )

export const heatmapConfigOptions: HeatmapConfigOption[] = [
  {
    name: 'Current Population',
    category: 'Population',
    field: 'cy_pop_c',
    type: 'breakdown',
    isTotal: true,
  },
  {
    name: 'Workday Population',
    category: 'Population',
    field: 'cy_work_day_count',
    type: 'simple',
  },
  {
    name: 'Total Households',
    category: 'Population',
    field: 'cy_househldsc',
    type: 'breakdown',
  },

  {
    name: '0 - 4',
    category: AGE_CATEGORY,
    field: 'cy_pop_0_4_c',
    type: 'breakdown',
  },
  {
    name: '5 - 9',
    category: AGE_CATEGORY,
    field: 'cy_pop_5_9_c',
    type: 'breakdown',
  },
  {
    name: '10 - 14',
    category: AGE_CATEGORY,
    field: 'cy_pop_10_14c',
    type: 'breakdown',
  },
  {
    name: '15 - 17',
    category: AGE_CATEGORY,
    field: 'cy_pop_15_17c',
    type: 'breakdown',
  },
  {
    name: '18 - 20',
    category: AGE_CATEGORY,
    field: 'cy_pop_18_20c',
    type: 'breakdown',
  },
  {
    name: '21 - 24',
    category: AGE_CATEGORY,
    field: 'cy_pop_21_24c',
    type: 'breakdown',
  },
  {
    name: '25 - 34',
    category: AGE_CATEGORY,
    field: 'cy_pop_25_34c',
    type: 'breakdown',
  },
  {
    name: '35 - 44',
    category: AGE_CATEGORY,
    field: 'cy_pop_35_44c',
    type: 'breakdown',
  },
  {
    name: '45 - 54',
    category: AGE_CATEGORY,
    field: 'cy_pop_45_54c',
    type: 'breakdown',
  },
  {
    name: '55 - 64',
    category: AGE_CATEGORY,
    field: 'cy_pop_55_64c',
    type: 'breakdown',
  },
  {
    name: '65 - 74',
    category: AGE_CATEGORY,
    field: 'cy_pop_65_74c',
    type: 'breakdown',
  },
  {
    name: '75 - 84',
    category: AGE_CATEGORY,
    field: 'cy_pop_75_84c',
    type: 'breakdown',
  },
  {
    name: '85+',
    category: AGE_CATEGORY,
    field: 'cy_pop_85p_c',
    type: 'breakdown',
  },
  {
    name: 'Median Age',
    category: AGE_CATEGORY,
    field: 'cy_mage_c',
    type: 'simple',
  },
  {
    name: 'Average Age',
    category: AGE_CATEGORY,
    field: 'cy_av_age_c',
    type: 'simple',
  },
  {
    name: 'White',
    category: 'Race',
    field: 'cy_white_c',
    type: 'breakdown',
  },
  {
    name: 'Hispanic',
    category: 'Race',
    type: 'breakdown',
    field: 'cy_hispanic_c',
  },
  {
    name: 'Black',
    category: 'Race',
    type: 'breakdown',
    field: 'cy_black_c',
  },
  {
    name: 'Asian',
    category: 'Race',
    type: 'breakdown',
    field: 'cy_asian_c',
  },
  {
    name: 'Native American',
    category: 'Race',
    type: 'breakdown',
    field: 'cy_indian_c',
  },
  {
    name: 'Other',
    category: 'Race',
    type: 'breakdown',
    field: 'cy_other_c',
  },
  {
    field: 'cy_hi_l15_c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '<$15,000',
  },
  {
    field: 'cy_hi_15_25_c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$15,000-$24,999',
  },
  {
    field: 'cy_hi_25_35_c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$25,000-$34,999',
  },
  {
    field: 'cy_hi_35_50_c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$35,000-$49,999',
  },
  {
    field: 'cy_hi_50_75_c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$50,000-$74,999',
  },
  {
    field: 'cy_hi_75_100c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$75,000-$99,999',
  },
  {
    field: 'cy_hi100_125c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$100,000-$124,999',
  },
  {
    field: 'cy_hi125_150c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$125,000-$149,999',
  },
  {
    field: 'cy_hi150_200c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$150,000-$199,999',
  },
  {
    field: 'cy_hi200_250c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$200,000-$249,999',
  },
  {
    field: 'cy_hi250_500c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$250,000-$499,999',
  },
  {
    field: 'cy_hi_500p_c',
    type: 'breakdown',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: '$500,000+',
  },
  {
    field: 'cy_med_hinc_c',
    type: 'simple',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: 'Median Household income',
  },
  {
    field: 'cy_avg_hinc_c',
    type: 'simple',
    category: HOUSEHOLD_INCOME_CATEGORY,
    name: 'Average Household Income',
  },
  {
    field: 'cy_totale',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Total',
    isTotal: true,
  },
  {
    field: 'cy_tr',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Total Retail',
  },
  {
    field: 'cy_foodto',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Food',
  },
  {
    field: 'cy_alcbev',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Alcohol',
  },
  {
    field: 'cy_housin',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Housing',
  },
  {
    field: 'cy_appare',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Apparel',
  },
  {
    field: 'cy_trans',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Transportation',
  },
  {
    field: 'cy_health',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Health',
  },
  {
    field: 'cy_entrta',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Entertainment',
  },
  {
    field: 'cy_persca',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Personal Care',
  },
  {
    field: 'cy_educat',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Education',
  },
  {
    field: 'cy_tobacc',
    type: 'spend',
    category: CONSUMER_SPEND_CATEGORY,
    name: 'Tobacco',
  },
  {
    type: 'simple',
    category: TRAFFIC_CATEGORY,
    name: 'Total AADT',
    field: 'total_aadt',
  },
  {
    field: 'max_aadt',
    type: 'simple',
    category: TRAFFIC_CATEGORY,
    name: 'Maximum AADT',
  },
]
